import { useState, useCallback } from 'react';
import {
  Activity,
  ActivityResponse,
  SearchActivitiesRequest,
  ACLAuthorizationRequest,
  SearchActivitiesPostContentTypeEnum,
  CreateActivityContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getActivitiesApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseActivitiesReturn {
  activities: ActivityResponse[];
  loading: boolean;
  searchError: string | null;
  selectedActivity: ActivityResponse | null;
  setSelectedActivity: (activity: ActivityResponse | null) => void;
  fetchActivities: (page?: number, pageSize?: number, searchBody?: SearchActivitiesRequest) => Promise<boolean>;
  createActivity: (activityData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteActivity: (activityId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useActivities = (): UseActivitiesReturn => {
  const [items, setItems] = useState<ActivityResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<ActivityResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchActivities = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchActivitiesRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getActivitiesApi();
      const response = await api.searchActivitiesPost({
        contentType: SearchActivitiesPostContentTypeEnum.ApplicationJson,
        searchActivitiesRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch activities');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createActivity = useCallback(async (activityData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getActivitiesApi();

      const createdActivity = await api.createActivity({
        contentType: CreateActivityContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        activity: activityData as Activity,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdActivityId = createdActivity?.id;

      showSuccess(
        'Activity created successfully',
        createdActivityId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('activity', createdActivityId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create activity', 'Failed to create activity');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteActivity = useCallback(async (activityId: string) => {
    try {
      const api = getActivitiesApi();
      await api.deleteActivityById({ activityId });
      showSuccess('Activity deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete activity', 'Failed to delete activity');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    activities: items,
    loading,
    searchError,
    selectedActivity,
    setSelectedActivity,
    fetchActivities,
    createActivity,
    deleteActivity,
    setTotal,
    total,
  };
};
